import React from 'react'

interface VaultIconProps {
    className?: string
    size?: number
    variant?: 'default' | 'active' | 'hover'
}

export function VaultIcon({
    className = '',
    size = 24,
    variant = 'default'
}: VaultIconProps) {
    const colors = {
        default: {
            primary: '#94a3b8',
            secondary: '#64748b',
            accent: '#fb2ba9',
            glow: 'rgba(251, 43, 169, 0.2)'
        },
        active: {
            primary: '#fb2ba9',
            secondary: '#e91e8c',
            accent: '#fb2ba9',
            glow: 'rgba(251, 43, 169, 0.4)'
        },
        hover: {
            primary: '#cbd5e1',
            secondary: '#94a3b8',
            accent: '#fb2ba9',
            glow: 'rgba(251, 43, 169, 0.3)'
        }
    }

    const c = colors[variant]

    return (
        <>
            <style>{`
        /* Spin the central dial through the lighter gradient */
        @keyframes vault-dial-spin {
          0% { transform: rotate(0deg); fill: ${c.secondary}; }
          20% { fill: url(#vault-gradient); }
          80% { fill: url(#vault-gradient); }
          100% { transform: rotate(180deg); fill: #00FFC0; }
        }
        
        /* Slide the locking bolts outward to 'lock' into the frame */
        @keyframes bolt-deploy-left {
          0% { transform: translateX(0); }
          100% { transform: translateX(-3px); }
        }

        @keyframes bolt-deploy-right {
          0% { transform: translateX(0); }
          100% { transform: translateX(3px); }
        }

        .vault-hover .vault-dial {
          animation: vault-dial-spin 1.2s ease-in-out forwards;
          transform-origin: 12px 12.5px;
        }

        .vault-hover .bolt-l {
          animation: bolt-deploy-left 0.6s ease-out 0.6s forwards;
        }

        .vault-hover .bolt-r {
          animation: bolt-deploy-right 0.6s ease-out 0.6s forwards;
        }
      `}</style>

            <svg
                width={size}
                height={size}
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className={`${className} ${variant === 'hover' ? 'vault-hover' : ''}`}
            >
                <defs>
                    <linearGradient id="vault-gradient" x1="4" y1="4" x2="20" y2="20" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#CC339F" />
                        <stop offset="25%" stopColor="#A855F7" />
                        <stop offset="100%" stopColor="#00FFC0" />
                    </linearGradient>
                </defs>

                {/* Active Glow */}
                {variant === 'active' && (
                    <circle cx="12" cy="12" r="10" fill={c.glow} opacity="0.6" />
                )}

                {/* Outer Vault Frame - Thick and Secure */}
                <rect
                    x="4" y="4" width="16" height="17" rx="2"
                    stroke={c.secondary}
                    strokeWidth="2.5"
                    opacity="0.9"
                />

                {/* Locking Bolts - These 'slide' into the frame */}
                <rect className="bolt-l" x="6" y="11.5" width="4" height="2" rx="1" fill={c.secondary} />
                <rect className="bolt-r" x="14" y="11.5" width="4" height="2" rx="1" fill={c.primary} />

                {/* Central Dial - Spins through colors and holds Cyan */}
                <path
                    className="vault-dial"
                    d="M12 7.5a5 5 0 1 0 0 10 5 5 0 0 0 0-10zm0 2a3 3 0 1 1 0 6 3 3 0 0 1 0-6z"
                    fill={c.secondary}
                    stroke={variant === 'active' ? c.accent : 'transparent'}
                    strokeWidth="1"
                />

                {/* Dial Indicator (Little notch that shows it's spinning) */}
                <circle className="vault-dial" cx="12" cy="9.5" r="0.8" fill="currentColor" opacity="0.5" />

                {/* Shared Content Dots - The signature of this icon set */}
                <circle cx="7.5" cy="7.5" r="1" fill="currentColor" opacity="0.4" />
                <circle cx="7.5" cy="16.5" r="1" fill="currentColor" opacity="0.4" />
                <circle cx="16.5" cy="16.5" r="1" fill="currentColor" opacity="0.4" />

                {/* Accent dot for active state */}
                {variant === 'active' && (
                    <circle cx="19" cy="6" r="2" fill={c.accent} />
                )}
            </svg>
        </>
    )
}